// File: src/context/UserContext.tsx
// ИСПРАВЛЕННАЯ ВЕРСИЯ: Функция login теперь корректно работает с TokenResponse.

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TokenResponse } from '@react-oauth/google';
import i18n from '../i18n'; // Импортируем i18n напрямую
import { authWithGoogle, fetchUserProfile } from '../services/api';
import { User } from '../types';

interface UserContextType {
    user: User | null;
    isLoading: boolean;
    error: string | null;
    login: (tokenResponse: TokenResponse) => Promise<void>;
    logout: () => void;
    checkAuth: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setIsLoading(false);
            setUser(null);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const profile = await fetchUserProfile();
            setUser(profile);
        } catch (err) {
            setUser(null);
            localStorage.removeItem('token');
            // Можно добавить перевод для ошибки, но обычно здесь не показываем ошибки пользователю
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (tokenResponse: TokenResponse) => {
        setIsLoading(true);
        setError(null);
        try {
            const backendResponse = await authWithGoogle(tokenResponse.access_token);
            localStorage.setItem('token', backendResponse.access_token);

            await checkAuth();

            navigate('/dashboard');
        } catch (err) {
            // Используем i18n напрямую для перевода ошибок
            const errorMessage = err instanceof Error
                ? err.message
                : i18n.t('user_context.login_failed');

            setError(errorMessage);
            setUser(null);
            localStorage.removeItem('token');
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem('token');
        navigate('/');
    };

    const value = { user, isLoading, error, login, logout, checkAuth };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        // Это сообщение об ошибке разработчика, поэтому не переводим
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
